import { Alert, AlertDescription } from '@brent/core-ui/ui'
import { useLiveQuestion, useMyVote, useVoteResults, useCastVote } from '@/hooks/useQuestions'
import VoteForm from '@/components/features/voting/VoteForm'
import VoteResults from '@/components/features/voting/VoteResults'
import MembersOnlyNotice from '@/components/shared/MembersOnlyNotice'
import { useAuth } from '@/hooks/useAuth'

export default function Voting() {
  const { hasGroup } = useAuth()
  const isMember = hasGroup('member')
  const {
    data: question,
    isLoading: questionLoading,
    error: questionError,
  } = useLiveQuestion(isMember)
  const { data: myVote } = useMyVote(question?.question_id)
  const hasVoted = !!myVote
  const { data: results } = useVoteResults(question?.question_id, hasVoted)
  const castVoteMutation = useCastVote()

  if (!isMember) {
    return <MembersOnlyNotice title="Voting" />
  }

  const handleVote = (optionId: string) => {
    if (!question) return
    castVoteMutation.mutate({ questionId: question.question_id, optionId })
  }

  if (questionLoading) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl sm:text-3xl font-bold">Voting</h1>
        <div className="h-48 animate-pulse rounded-lg bg-muted" />
      </div>
    )
  }

  if (questionError) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl sm:text-3xl font-bold">Voting</h1>
        <Alert>
          <AlertDescription>
            There is no active vote at the moment. Check back later!
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  if (!question) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl sm:text-3xl font-bold">Voting</h1>
        <Alert>
          <AlertDescription>
            There is no active vote at the moment. Check back later!
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl sm:text-3xl font-bold">Voting</h1>

      <VoteForm
        question={question}
        currentOptionId={myVote?.option_id}
        isPending={castVoteMutation.isPending}
        onSubmit={handleVote}
      />

      {castVoteMutation.isError && (
        <Alert variant="destructive">
          <AlertDescription>
            {castVoteMutation.error instanceof Error
              ? castVoteMutation.error.message
              : 'Failed to submit vote. Please try again.'}
          </AlertDescription>
        </Alert>
      )}

      {results && <VoteResults options={question.options} results={results} />}
    </div>
  )
}
